<script setup>
import { ref } from 'vue'
import { RouterLink, RouterView, useRouter } from 'vue-router'
import flagUkraine from './assets/flags/ukraine.svg'
import flagRussia from './assets/flags/russia.svg'
import flagUnitedKingdom from './assets/flags/united-kingdom.svg'
import { useI18n } from './i18n'
import { changeReportLanguage, session, signOut } from './session'

const router = useRouter()
const { t } = useI18n()
const desktopBreakpoint = 1024
const sidebarOpen = ref(typeof window === 'undefined' || window.innerWidth >= desktopBreakpoint)
const languageSaving = ref(false)
const languageError = ref('')
const languageMenu = ref(null)
const reportLanguages = [
  { code: 'uk', labelKey: 'ukrainian', flag: flagUkraine },
  { code: 'ru', labelKey: 'russian', flag: flagRussia },
  { code: 'en', labelKey: 'english', flag: flagUnitedKingdom },
]

function closeSidebar() {
  sidebarOpen.value = false
}

function closeSidebarAfterNavigation() {
  if (window.innerWidth < desktopBreakpoint) closeSidebar()
}

function roleLabel(role) {
  return role === 'ADMIN' ? t('administrator') : t('manager')
}

function activeReportLanguage() {
  return reportLanguages.find((language) => language.code === session.user?.reportLanguage) ?? reportLanguages[0]
}

function closeLanguageMenu() {
  languageMenu.value?.removeAttribute('open')
}

async function setReportLanguage(language) {
  if (language === session.user?.reportLanguage) {
    closeLanguageMenu()
    return
  }
  languageSaving.value = true
  languageError.value = ''
  try {
    await changeReportLanguage(language)
    if (router.currentRoute.value.name === 'report-detail') {
      await router.push({ name: 'home' })
    }
  } catch (error) {
    languageError.value = t('languageChangeFailed')
  } finally {
    languageSaving.value = false
    closeLanguageMenu()
  }
}

async function logout() {
  try {
    await signOut()
  } finally {
    closeSidebar()
    router.push({ name: 'login' })
  }
}
</script>

<template>
  <div class="application-shell" :class="{ 'login-shell': !session.user, 'sidebar-expanded': session.user && sidebarOpen }">
    <header v-if="session.user" class="application-header">
      <div class="brand-strip"><RouterLink to="/reports">Next Reports <span>Web</span></RouterLink></div>
      <div class="topbar">
        <button class="menu-toggle" type="button" :aria-expanded="sidebarOpen" aria-controls="application-navigation"
          :aria-label="sidebarOpen ? t('menuClose') : t('menuOpen')" @click="sidebarOpen = !sidebarOpen">
          <span></span><span></span><span></span>
        </button>
        <RouterLink class="brand" to="/reports">
          <strong>Next Reports</strong>
          <small>{{ t('reportingSystem') }}</small>
        </RouterLink>
        <div class="header-spacer"></div>
        <details ref="languageMenu" class="language-menu">
          <summary class="language-menu-trigger" :aria-label="t('language')" :title="t(activeReportLanguage().labelKey)">
            <img :src="activeReportLanguage().flag" alt="" />
          </summary>
          <div class="language-popover" :aria-label="t('language')" role="group">
            <button v-for="language in reportLanguages" :key="language.code" class="language-option" type="button"
              :class="{ active: session.user.reportLanguage === language.code }"
              :aria-label="t(language.labelKey)" :disabled="languageSaving || session.user.mustChangePassword"
              @click="setReportLanguage(language.code)">
              <img :src="language.flag" alt="" />
              <span>{{ t(language.labelKey) }}</span>
            </button>
          </div>
        </details>
        <span v-if="languageError" class="language-error" role="alert">{{ languageError }}</span>
        <details class="profile-menu">
          <summary class="profile-trigger" :aria-label="t('profileMenu')">
            <span class="profile-avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4.4 0-8 2.2-8 5v1h16v-1c0-2.8-3.6-5-8-5Z" /></svg>
            </span>
            <span class="profile-identity"><strong>{{ session.user.username }}</strong><small>{{ roleLabel(session.user.role) }}</small></span>
            <span class="profile-chevron" aria-hidden="true">⌄</span>
          </summary>
          <div class="profile-popover">
            <RouterLink to="/change-password" @click="closeSidebarAfterNavigation">{{ t('changePassword') }}</RouterLink>
            <button type="button" @click="logout">{{ t('signOut') }}</button>
          </div>
        </details>
      </div>
    </header>
    <Transition name="sidebar">
      <button v-if="session.user && sidebarOpen" class="sidebar-backdrop" type="button" :aria-label="t('menuClose')" @click="closeSidebar"></button>
    </Transition>
    <Transition name="sidebar-panel">
      <aside v-if="session.user && sidebarOpen" id="application-navigation" class="app-sidebar">
        <button class="sidebar-close-button" type="button" :aria-label="t('menuClose')" @click="closeSidebar">×</button>
        <nav :aria-label="t('navigation')">
          <p class="sidebar-section-label">{{ t('work') }}</p>
          <RouterLink to="/reports" @click="closeSidebarAfterNavigation">{{ t('reports') }}</RouterLink>
          <div class="sidebar-divider"></div>
          <p class="sidebar-section-label">{{ t('administration') }}</p>
          <RouterLink to="/admin/stations" @click="closeSidebarAfterNavigation">{{ t('stations') }}</RouterLink>
          <RouterLink to="/admin/users" @click="closeSidebarAfterNavigation">{{ t('users') }}</RouterLink>
          <RouterLink to="/admin/monitoring" @click="closeSidebarAfterNavigation">{{ t('monitoring') }}</RouterLink>
        </nav>
      </aside>
    </Transition>
    <main class="page" :class="{ 'workspace-page': router.currentRoute.value.name === 'home' }"><RouterView /></main>
  </div>
</template>
